import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export const STATUS_STYLES = {
  Enquiry: "bg-amber-100 text-amber-800 border-amber-200",
  Confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Active: "bg-blue-100 text-blue-800 border-blue-200",
  Completed: "bg-slate-100 text-slate-600 border-slate-200",
};
export const STATUSES = Object.keys(STATUS_STYLES);

export const PAYMENT_STATUSES = ["Unpaid", "Deposit Taken", "Paid"];
export const PAYMENT_STYLES = {
  Unpaid: "bg-red-100 text-red-800 border-red-200",
  "Deposit Taken": "bg-amber-100 text-amber-800 border-amber-200",
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const EMPTY = { name: "", phone: "", email: "", address: "", hire_start: "", hire_end: "", status: "Enquiry", payment_status: "Unpaid", price: "", notes: "" };

function ClientDialog({ client, onClose, onSaved }) {
  const [form, setForm] = useState(client ? { ...EMPTY, ...client, hire_start: client.hire_start || "", hire_end: client.hire_end || "" } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1A237E]";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, hire_start: form.hire_start || null, hire_end: form.hire_end || null, price: Number(form.price) || 0 };
    try {
      if (client) {
        await api.put(`/admin/clients/${client.id}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/admin/clients", payload);
        toast.success("Client added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="client-form-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{client ? "Edit Client" : "Add Client"}</h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid="client-dialog-close"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="client-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="client-phone-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="client-email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select value={form.status} onChange={set("status")} className={inputCls} data-testid="client-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
            <input value={form.address} onChange={set("address")} className={inputCls} data-testid="client-address-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hire Start Date</label>
            <input type="date" value={form.hire_start} onChange={set("hire_start")} className={inputCls} data-testid="client-hire-start-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hire End Date</label>
            <input type="date" value={form.hire_end} onChange={set("hire_end")} className={inputCls} data-testid="client-hire-end-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Job Price (£)</label>
            <input type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className={inputCls} data-testid="client-price-input" placeholder="0.00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payment Status</label>
            <select value={form.payment_status} onChange={set("payment_status")} className={inputCls} data-testid="client-payment-status-select">
              {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea rows={3} value={form.notes} onChange={set("notes")} className={inputCls} data-testid="client-notes-input" />
          </div>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="client-cancel-button">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-[#1A237E] rounded-md hover:bg-[#121A5C] disabled:opacity-60" data-testid="client-save-button">
              {saving ? "Saving…" : client ? "Save Changes" : "Add Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [dialog, setDialog] = useState(null); // null | "new" | client object

  const load = () => {
    api.get("/admin/clients").then((res) => setClients(res.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (client) => {
    if (!window.confirm(`Delete ${client.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/clients/${client.id}`);
      toast.success("Client deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const shown = filter === "All" ? clients : clients.filter((c) => c.status === filter);

  return (
    <div data-testid="client-tracker-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Client Tracker</h1>
          <p className="text-sm text-slate-500 mt-1">All enquiries and clients — {clients.length} total</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="status-filter-select">
            <option value="All">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => setDialog("new")} className="flex items-center gap-2 px-4 py-2 bg-[#1A237E] text-white text-sm font-semibold rounded-md hover:bg-[#121A5C]" data-testid="add-client-button">
            <Plus className="h-4 w-4" /> Add Client
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Address</th>
                <th className="px-4 py-3">Hire Start</th>
                <th className="px-4 py-3">Hire End</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400" data-testid="clients-loading">Loading…</td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400" data-testid="clients-empty">No clients found.</td></tr>
              ) : (
                shown.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{c.address || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.hire_start || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.hire_end || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${STATUS_STYLES[c.status] || STATUS_STYLES.Enquiry}`} data-testid={`client-status-${c.id}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full border ${PAYMENT_STYLES[c.payment_status] || PAYMENT_STYLES.Unpaid}`} data-testid={`client-payment-${c.id}`}>
                        {c.payment_status || "Unpaid"}{Number(c.price) > 0 ? ` · £${Number(c.price).toLocaleString("en-GB")}` : ""}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setDialog(c)} className="p-1.5 text-slate-400 hover:text-[#1A237E]" aria-label="Edit" data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(c)} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Delete" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {dialog && (
        <ClientDialog client={dialog === "new" ? null : dialog} onClose={() => setDialog(null)} onSaved={load} />
      )}
    </div>
  );
}
